import { createContext, use } from 'react'
import type { ReactNode } from 'react'
import { useViewer, type Viewer } from '@lb/domain'
import { useTranslation } from '@lb/i18n'

import { http } from '../../lib/client'

interface SessionContextValue {
  viewer: Viewer | null
  isLoading: boolean
  isError: boolean
}

const SessionContext = createContext<SessionContextValue | null>(null)

/**
 * Session and entitlement, above the router (spec §2, architecture §6.5).
 *
 * Above the router because route guards read it: a guard that resolved after
 * the route had already rendered would flash a screen the member cannot use.
 */
export function SessionProvider({ children }: { children: ReactNode }) {
  /*
   * The interface language, passed to the entitlement read so `planName`
   * answers in it (round 14). `useContentLocale` is not used here: this
   * provider sits ABOVE the router, and that hook reads the route.
   */
  const { i18n } = useTranslation()
  const state = useViewer(http, i18n.language === 'en' ? 'en' : 'fr')
  return <SessionContext value={state}>{children}</SessionContext>
}

export function useSessionContext(): SessionContextValue {
  const value = use(SessionContext)
  if (!value) throw new Error('useSessionContext must be used inside <SessionProvider>')
  return value
}

/** The signed-in member, or null. */
export function useViewerOrNull(): Viewer | null {
  return useSessionContext().viewer
}
